import sql from 'highlight.js/lib/languages/sql';
import python from 'highlight.js/lib/languages/python';
import hljs from 'highlight.js';
import { common, createLowlight } from 'lowlight';
import TTCodeBlockLowLight from '@tiptap/extension-code-block-lowlight';

/**
  To add support for other languages in code blocks, add the language to the
  CODE_BLOCK_LANGUAGES object below. You can also redefine colors for highlights.
  For example, to add support for TS:
  ```javascript
  import ts from 'highlight.js/lib/languages/typescript';
  export const CODE_BLOCK_LANGUAGES = {
    // ...
    ts: {
      lang: 'typescript',
      key: 'ts', // represents CODE_BLOCK_LANGUAGES[key]
      lib: ts,
      colors: {
        keyword: colorsButton.blue.default,
        attr: colorsButton.green.default,
        comment: colorsButton.green.default,
        operator: colorsButton.red.default,
        // etc
      },
    },
  }
  ```
    And then you can pass the `CODE_BLOCK_LANGUAGES.ts.key` as `codeBlockLanguage` prop of the BasicRichTextEditor
  to enable sintax highlighting for typescript.
  By default `disableCodeBlockEscape` prop of the `BasicRichTextEditor` is `true`. That means that Command + Enter or Ctrl + Enter is disabled, the content passed to the editor will be wrapped in a code block with the language specified in the `codeBlockLanguage` prop. If you want to maintain mixed content (rich text and code bloks), pass `false` to the `disableCodeBlockEscape` prop of the BasicRichTextEditor. If `disableCodeBlockEscape` is `true`, we need content to be always wrapped in `<pre><code></code><pre>`, so if `initialValue` is empty or plain text pass it to `BasicRichTextEditor` as `initialValue={toCodeBlock(initialValue, CODE_BLOCK_LANGUAGES[language].lang)}`. 

  ```javascript
  <BasicRichTextEditor
    initialValue={toCodeBlock(
      value || '',
      CODE_BLOCK_LANGUAGES.ts.lang
    )}
    codeBlockLanguage={CODE_BLOCK_LANGUAGES.ts.key}
  />
  ```
 */
export const CODE_BLOCK_LANGUAGES = {
  sql: {
    lang: 'sql',
    key: 'sql',
    lib: sql,
  },
  python: {
    lang: 'python',
    key: 'python',
    lib: python,
    colors: {
      params: 'green',
    },
  },
};

const lowlight = createLowlight(common);

for (const { lang, lib } of Object.values(CODE_BLOCK_LANGUAGES)) {
  lowlight.register({ [lang]: lib });
}

export const toCodeBlock = (value = '', language = 'sql') => {
  return `<pre><code class="hljs language-${language}">${hljs.highlight(value, { language }).value}</code></pre>`;
};

export const getCodeBlockExtension = (
  codeBlockLanguage,
  disableCodeBlockEscape
) => {
  if (codeBlockLanguage && CODE_BLOCK_LANGUAGES[codeBlockLanguage]) {
    return TTCodeBlockLowLight.extend({
      // disable exiting the code block with Ctrl + Enter / Command + Enter or Shift + Enter
      addKeyboardShortcuts() {
        return disableCodeBlockEscape
          ? {
              'Mod-Enter': () => true,
              'Shift-Enter': () => true,
            }
          : {};
      },
    }).configure({
      lowlight,
      defaultLanguage: CODE_BLOCK_LANGUAGES[codeBlockLanguage].lang,
    });
  }

  return null;
};

export const getOnChange = (onChange, disableCodeBlockEscape) => {
  return (...args) => {
    if (disableCodeBlockEscape) {
      const { editor } = args[0];
      if (
        !editor.getText() &&
        editor.getJSON().content?.[0]?.type !== 'codeBlock'
      ) {
        editor.chain().setCodeBlock().run();
      }
    }
    onChange?.(...args);
  };
};
